import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { openRazorpayCheckout } from '../utils/razorpay';
import { trackFunnel } from '../api';
import {
  FileCheck2, Check, Zap, Clock, Eye, Download, ArrowLeft, Crown, Building2, Sparkles, Mail, Bell, CreditCard
} from 'lucide-react';

const plans = [
  {
    name: 'Free',
    price: '₹0',
    period: '/month',
    desc: 'Get started with AI-powered QA.',
    features: [
      'Up to 50 Requirements',
      'Up to 200 Scenarios',
      'Up to 500 Test Cases',
      'Basic AI generation',
      'Export (Excel only)',
    ],
    cta: 'Get Started Free',
    highlight: false,
    icon: Zap,
  },
  {
    name: 'Pro',
    price: '₹499',
    originalPrice: '₹999',
    period: '/month',
    desc: 'For growing QA teams shipping faster.',
    offerLabel: 'Limited Early Offer',
    features: [
      'Up to 500 Requirements',
      'Up to 2,000 Scenarios',
      'Up to 5,000 Test Cases',
      'Full AI generation (Requirements, Scenarios, Test Cases)',
      'Traceability Matrix',
      'Export (Excel + Jira/Zephyr)',
      'Priority processing',
    ],
    cta: 'Upgrade to Pro',
    highlight: true,
    badge: 'Most Popular',
    icon: Crown,
  },
  {
    name: 'Enterprise',
    price: 'Custom',
    period: '',
    desc: 'For large teams with custom needs.',
    features: [
      'Unlimited Requirements, Scenarios, Test Cases',
      'Dedicated support',
      'Team collaboration (coming soon)',
      'Custom integrations',
    ],
    cta: 'Contact Us',
    highlight: false,
    icon: Building2,
  },
];

const whyItems = [
  { icon: Clock, text: 'Save 80% QA effort' },
  { icon: Sparkles, text: 'Generate test cases in minutes' },
  { icon: Eye, text: 'Improve coverage with AI' },
  { icon: Download, text: 'Export ready for Jira/Zephyr' },
];

export const PricingPage = () => {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // Funnel: pricing_viewed (once per page load)
  useEffect(() => {
    trackFunnel('pricing_viewed');
  }, []);

  const handleCta = (plan) => {
    if (plan.name === 'Free') {
      navigate(user ? '/dashboard' : '/signup');
      return;
    }
    if (!user) {
      toast.error('Please login to continue with upgrade');
      navigate('/login');
      return;
    }
    // Funnel: upgrade_clicked
    trackFunnel('upgrade_clicked', { plan: plan.name });
    setShowUpgradeModal(true);
  };

  return (
    <div className="min-h-screen bg-white" data-testid="pricing-page">
      {/* Minimal nav */}
      <nav className="border-b border-slate-100 bg-white/80 backdrop-blur sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
          <button onClick={() => navigate(user ? '/dashboard' : '/login')} className="flex items-center gap-2 text-slate-800 hover:text-blue-600 transition-colors" data-testid="pricing-nav-logo">
            <div className="p-1.5 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-lg">
              <FileCheck2 className="h-4 w-4 text-white" />
            </div>
            <span className="font-heading font-bold text-lg">TestGenie AI</span>
          </button>
          <div className="flex items-center gap-3">
            {!user && (
              <>
                <Button variant="ghost" size="sm" onClick={() => navigate('/login')} data-testid="pricing-nav-login">Log in</Button>
                <Button size="sm" onClick={() => navigate('/signup')} className="bg-blue-600 hover:bg-blue-500 text-white" data-testid="pricing-nav-signup">Sign up free</Button>
              </>
            )}
            {user && (
              <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} className="gap-1.5" data-testid="pricing-nav-dashboard">
                <ArrowLeft className="h-3.5 w-3.5" /> Dashboard
              </Button>
            )}
          </div>
        </div>
      </nav>

      {/* Header */}
      <section className="pt-16 pb-4 text-center px-4">
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-slate-900 tracking-tight" data-testid="pricing-heading">
          Simple, Transparent Pricing
        </h1>
        <p className="mt-3 text-base sm:text-lg text-slate-500 max-w-lg mx-auto" data-testid="pricing-subheading">
          Start free. Upgrade as your QA needs grow.
        </p>
      </section>

      {/* Plans grid */}
      <section className="py-12 px-4">
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
          {plans.map((plan) => (
            <Card
              key={plan.name}
              className={`relative p-6 sm:p-8 rounded-2xl flex flex-col ${
                plan.highlight
                  ? 'border-2 border-blue-600 shadow-xl shadow-blue-600/10 scale-[1.02]'
                  : 'border border-slate-200 shadow-sm'
              }`}
              data-testid={`plan-card-${plan.name.toLowerCase()}`}
            >
              {plan.badge && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 bg-blue-600 text-white text-xs font-semibold rounded-full" data-testid="popular-badge">
                  {plan.badge}
                </span>
              )}
              <div className="mb-6">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-4 ${plan.highlight ? 'bg-blue-600' : 'bg-slate-100'}`}>
                  <plan.icon className={`h-5 w-5 ${plan.highlight ? 'text-white' : 'text-slate-600'}`} />
                </div>
                <h3 className="text-xl font-heading font-bold text-slate-900">{plan.name}</h3>
                <p className="text-sm text-slate-500 mt-1">{plan.desc}</p>
              </div>
              <div className="mb-6">
                {plan.originalPrice && (
                  <span className="text-lg text-slate-400 line-through mr-2">{plan.originalPrice}</span>
                )}
                <span className="text-4xl font-bold text-slate-900">{plan.price}</span>
                {plan.period && <span className="text-slate-500 text-base">{plan.period}</span>}
                {plan.offerLabel && (
                  <span className="ml-2 inline-block text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">{plan.offerLabel}</span>
                )}
              </div>
              <ul className="space-y-3 mb-8 flex-1">
                {plan.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm text-slate-700">
                    <Check className={`h-4 w-4 mt-0.5 flex-shrink-0 ${plan.highlight ? 'text-blue-600' : 'text-emerald-500'}`} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <Button
                onClick={() => handleCta(plan)}
                className={`w-full h-11 font-semibold rounded-xl ${
                  plan.highlight
                    ? 'bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-600/20'
                    : 'bg-slate-900 hover:bg-slate-800 text-white'
                }`}
                data-testid={`plan-cta-${plan.name.toLowerCase()}`}
              >
                {plan.cta}
              </Button>
            </Card>
          ))}
        </div>
      </section>

      {/* Why choose */}
      <section className="py-16 bg-slate-50 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-slate-900 mb-10" data-testid="why-heading">
            Why choose TestGenie?
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {whyItems.map((item, i) => (
              <div key={i} className="flex items-center gap-3 p-4 bg-white border border-slate-100 rounded-xl" data-testid={`why-item-${i}`}>
                <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                  <item.icon className="h-4 w-4 text-blue-600" />
                </div>
                <span className="text-slate-800 font-medium text-sm">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-6 bg-slate-900 text-center">
        <p className="text-sm text-slate-400 mb-1">Contact: <a href="mailto:gettestgenie@gmail.com" className="text-blue-400 hover:text-blue-300 underline" data-testid="footer-contact-email">gettestgenie@gmail.com</a></p>
        <p className="text-sm text-slate-500">TestGenie AI — AI-powered QA documentation for modern teams.</p>
      </footer>

      {/* Upgrade to Pro Modal */}
      <Dialog open={showUpgradeModal} onOpenChange={setShowUpgradeModal}>
        <DialogContent className="sm:max-w-md" data-testid="upgrade-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Crown className="h-5 w-5 text-blue-600" /> Upgrade to Pro
            </DialogTitle>
            <DialogDescription className="text-base pt-2">
              Unlock higher limits and priority AI processing for <span className="font-semibold text-slate-900">₹499/month</span>.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col sm:flex-row gap-2 pt-4">
            <Button variant="outline" onClick={() => setShowUpgradeModal(false)} className="gap-2" data-testid="upgrade-cancel-btn">
              Cancel
            </Button>
            <Button onClick={() => {
              setShowUpgradeModal(false);
              openRazorpayCheckout({
                userName: user?.full_name || '',
                userEmail: user?.email || '',
                onSuccess: () => {
                  toast.success('Payment successful');
                  refreshUser();
                  navigate('/dashboard');
                },
                onFailure: () => {
                  toast.error('Payment failed, please try again');
                },
              });
            }} className="gap-2 bg-blue-600 hover:bg-blue-500" data-testid="upgrade-pay-btn">
              <CreditCard className="h-4 w-4" /> Pay ₹499 Now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
